/**
 * catalogo-articulos.ts
 * Compras (REQUI) module — Catálogo de Artículos section: item_master admin
 * CRUD/bulk upsert, and the normalization queue that promotes free-text case
 * items into item_master rows.
 *
 * refactor/compras-by-section — pure move out of index.ts (no shape/behavior change).
 */
import { z } from 'zod';
import { IvaRateSchema } from '@suite/contracts-kernel';

import { UnitSchema, ITEM_KINDS } from './shared.js';

// ---------------------------------------------------------------------------
// Artículos (item_master) — admin CRUD (migration 0129 table, renamed to
// item_master + given a real ABM by migration 0137). "Artículos" is the UI
// name — deliberately not "catálogo", which already means the per-tenant
// config catalogs in this app (cost centers, categories, etc).
// ---------------------------------------------------------------------------

export const ItemMasterCreateSchema = z.object({
  /** Unique per tenant (uq_item_master_code_tenant) — 409 on conflict. */
  code: z.string().min(1, 'code is required').max(64, 'code must be at most 64 characters'),
  description: z.string().min(1, 'description is required'),
  categoryId: z.string().uuid(),
  subrubro: z.string().optional(),
  kind: z.enum(ITEM_KINDS),
  unit: UnitSchema,
  /** VAT rate (a percentage, IVA_RATES). null/absent = the general rate (DEFAULT_IVA_RATE). */
  ivaRate: IvaRateSchema.nullable().optional(),
});
export type ItemMasterCreate = z.infer<typeof ItemMasterCreateSchema>;

/** Every field editable except id/companyId — code stays editable here (unlike catalog.client_type's immutable code), since it's an operational sku, not a stable system identifier. */
export const ItemMasterUpdateSchema = ItemMasterCreateSchema.partial();
export type ItemMasterUpdate = z.infer<typeof ItemMasterUpdateSchema>;

export const ItemMasterResponseSchema = z.object({
  id: z.string().uuid(),
  companyId: z.string().uuid(),
  code: z.string().nullable(),
  description: z.string(),
  categoryId: z.string().uuid(),
  subrubro: z.string().nullable(),
  kind: z.enum(ITEM_KINDS),
  unit: UnitSchema,
  /** null = the general rate (DEFAULT_IVA_RATE). */
  ivaRate: z.number().nullable(),
  active: z.boolean(),
  createdAt: z.string(),
});
export type ItemMasterResponse = z.infer<typeof ItemMasterResponseSchema>;

/** POST /compras/item-master/bulk row shape — same fields as create, used for CSV/xlsx import upsert-by-code. */
export const ItemMasterBulkRowSchema = ItemMasterCreateSchema;
export type ItemMasterBulkRow = z.infer<typeof ItemMasterBulkRowSchema>;

export const ItemMasterBulkRequestSchema = z.object({
  items: z.array(ItemMasterBulkRowSchema).min(1),
});
export type ItemMasterBulkRequest = z.infer<typeof ItemMasterBulkRequestSchema>;

export const ItemMasterBulkResultSchema = z.object({
  created: z.array(ItemMasterResponseSchema),
  updated: z.array(ItemMasterResponseSchema),
  rejected: z.array(z.object({ row: z.number(), code: z.string().optional(), reason: z.string() })),
});
export type ItemMasterBulkResult = z.infer<typeof ItemMasterBulkResultSchema>;

// ---------------------------------------------------------------------------
// Cola de normalización (alta-solicitud.md §3.3, ★ precisión 2026-08-13:
// agrupación por EXACTO normalizado, sin fuzzy matching) — GET/POST under
// /compras/item-master/normalization-queue, roleGuard('admin') — same guard
// as the rest of the Artículos ABM this queue promotes INTO
// (item-master-admin.routes.ts), and lives in the same /admin/catalogs area
// on the frontend as ItemMasterManager.vue.
// ---------------------------------------------------------------------------

/** One exact variant inside a group — §3.3: "la cola siempre muestra ... las variantes exactas de cada grupo". */
export const NormalizationVariantSchema = z.object({
  itemId: z.string().uuid(),
  caseId: z.string().uuid(),
  description: z.string(),
  qty: z.string(),
  unit: z.string(),
});
export type NormalizationVariant = z.infer<typeof NormalizationVariantSchema>;

/** Case_items sharing the same normalized_description (migration 0138). Only groups with >1 occurrence — a single free-text item has nothing to promote-from-repetition yet. */
export const NormalizationGroupSchema = z.object({
  normalizedDescription: z.string(),
  occurrenceCount: z.number(),
  variants: z.array(NormalizationVariantSchema),
});
export type NormalizationGroup = z.infer<typeof NormalizationGroupSchema>;

export const NormalizationQueueResponseSchema = z.object({
  groups: z.array(NormalizationGroupSchema),
});
export type NormalizationQueueResponse = z.infer<typeof NormalizationQueueResponseSchema>;

/** POST body — comprador may deselect some variants of a group before promoting the rest. */
export const PromoteToItemMasterRequestSchema = z.object({
  itemIds: z.array(z.string().uuid()).min(1),
  categoryId: z.string().uuid(),
  unit: UnitSchema,
});
export type PromoteToItemMasterRequest = z.infer<typeof PromoteToItemMasterRequestSchema>;

/** Minimal per-item confirmation — the caller already has the full case_item elsewhere; this only confirms what changed. */
export const PromotedCaseItemSchema = z.object({
  id: z.string().uuid(),
  caseId: z.string().uuid(),
  itemMasterId: z.string().uuid().nullable(),
  categoryId: z.string().uuid().nullable(),
  pendienteNormalizar: z.boolean(),
});
export type PromotedCaseItem = z.infer<typeof PromotedCaseItemSchema>;

export const PromoteToItemMasterResponseSchema = z.object({
  itemMaster: ItemMasterResponseSchema,
  items: z.array(PromotedCaseItemSchema),
});
export type PromoteToItemMasterResponse = z.infer<typeof PromoteToItemMasterResponseSchema>;
